// src/pages/shop/ShopProducts.jsx
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  products as productsApi, categories as categoriesApi,
  upload as uploadApi, stockOps as stockOpsApi,
} from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney, getCurrencySymbol } from '../../data/currencies';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { isModuleOn, canSeeModule } from '../../config/modules';
import CategoryPickerSheet from '../../components/CategoryPickerSheet';
import { useAuth } from '../../hooks/useAuth';
import { extractGtin } from '../../utils/marking';
import { generateUniqueBarcode, generateBarcodeSync, isGeneratedBarcode } from '../../utils/generateBarcode';
import NumPad, { parseQty } from '../../components/NumPad';
import { useToast } from '../../hooks/useToast';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { useDeletePin } from '../../hooks/useDeletePin';
import ComponentPickerSheet from '../../components/ComponentPickerSheet';
import PhotoUploader from '../../components/PhotoUploader';
import AiGenerateSheet from '../../components/AiGenerateSheet';
import ScanButton from '../../components/ScanButton';
import { isComposite, computeAvailable, getMainPhoto } from '../../utils/composite';
import { SHOP_NAV, useShopNav } from './ShopHome';
import ShareProductDialog from '../../components/ShareProductDialog';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

// ─── Быстрые фильтры каталога ───────────────────────────────
// У товара с вариантами смотрим на сами варианты.
const activeVariants = (p) => {
  const vs = Array.isArray(p.variants) ? p.variants.filter(v => v.active !== false) : [];
  return vs.length > 1 ? vs : null;
};
const noPositive = (x) => !(Number(x) > 0);
const barcodesOf = (p) => [p.barcode, ...(activeVariants(p) || []).map(v => v.barcode)]
  .map(b => String(b || '').trim()).filter(Boolean);

const QUICK_FILTERS = {
  noPrice: (p) => {
    const vs = activeVariants(p);
    return vs ? vs.some(v => noPositive(v.salePrice)) : noPositive(p.salePrice);
  },
  // Услуги и составные (себестоимость считается из компонентов) не трогаем
  noCost: (p) => {
    if (p.isService || isComposite(p)) return false;
    const vs = activeVariants(p);
    return vs ? vs.some(v => noPositive(v.costPrice ?? p.costPrice)) : noPositive(p.costPrice);
  },
  noBarcode:  (p) => !p.isService && barcodesOf(p).length === 0,
  genBarcode: (p) => barcodesOf(p).some(isGeneratedBarcode),
};

export default function ShopProducts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { show, ToastView } = useToast();
  const { confirm: confirmDelete, PinModal } = useDeletePin();
  const navItems = useShopNav();
  const currency = useMyCurrency();
  const shop = useMyShop();
  const [items, setItems]     = useState([]);
  const [cats, setCats]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null); // product to share or null

  // Меню действий
  const [showActions, setShowActions] = useState(false);
  // Открытая операция со складом: 'purchase' | 'return' | 'writeoff'
  const [opType, setOpType] = useState(null);

  useEffect(() => { load(true); }, []);

  // Из аналитики «Завести товар» по коду, который пробивали как «ноунэйм»:
  // сразу форма нового товара с этим штрихкодом. state чистим, чтобы
  // «назад» не открывал форму повторно.
  useEffect(() => {
    const code = location.state && location.state.newBarcode;
    if (!code) return;
    setEditing({ _newBarcode: code });
    navigate(location.pathname, { replace: true, state: null });
  }, []); // eslint-disable-line


  const load = async (fromCache = false) => {
    // Есть прошлый список — показываем сразу, свежий подтянется ниже
    const cachedItems = fromCache ? productsApi.cached() : null;
    if (cachedItems) {
      setItems(cachedItems);
      setCats(categoriesApi.cached() || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const [list, catList] = await Promise.all([
        productsApi.list(),
        categoriesApi.list().catch(() => []),
      ]);
      setItems(list);
      setCats(catList);
    } catch (e) { show(e.message, 'error'); }
    setLoading(false);
  };

  // Лёгкая перезагрузка только товаров (без спиннера во весь экран) —
  // для обновления остатков после операций склада.
  const reloadProducts = async () => {
    try {
      const list = await productsApi.list();
      setItems(list);
    } catch (e) { /* не критично */ }
  };

  // Обновление при возврате на страницу из фона (макс. раз в 30 сек)
  useReloadOnFocus(reloadProducts);

  const remove = async (p) => {
    const ok = await confirmDelete({
      title: t('products.deleteTitle', 'Удалить товар?'),
      message: t('products.confirmDelete', { name: p.name }),
    });
    if (!ok) return;
    try { await productsApi.remove(p.id); show(t('products.deleted')); load(); }
    catch (e) { show(e.message, 'error'); }
  };

  // Обработчик сканирования штрихкода в поле поиска.
  // Если товар найден → открываем редактор; не найден → создаём новый
  // с предзаполненным баркодом.
  const handleScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      // Нашли — открываем редактор товара
      if (r?.product) {
        setEditing(r.product);
        return;
      }
    } catch (e) {
      // 404 — не найден, продолжим к созданию
      if (e?.status !== 404 && !/not_found/i.test(e?.message || '')) {
        show(e.message || 'Ошибка', 'error');
        return;
      }
    }
    // Не найден — открываем форму создания с предзаполненным баркодом
    show(t('products.barcodeNotFound', 'Товар с таким баркодом не найден. Создайте новый.'));
    setEditing({ _newBarcode: code });
  };

  // Фильтр «что недозаполнено» (без цены / без закупки / баркоды).
  // Кнопка фильтра видна, только пока есть такие товары — когда всё
  // заполнено, она сама исчезает и не мешает.
  const [quickFilter, setQuickFilter] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCounts = useMemo(() => {
    const c = { noPrice: 0, noCost: 0, genBarcode: 0, noBarcode: 0 };
    for (const p of items) {
      for (const k in c) if (QUICK_FILTERS[k](p)) c[k]++;
    }
    return c;
  }, [items]);
  const hasFilterItems = Object.values(filterCounts).some(n => n > 0);

  const searchLc = search.trim().toLowerCase();
  const filtered = items.filter(p =>
    (!quickFilter || QUICK_FILTERS[quickFilter](p)) && (
      !searchLc ||
      p.name.toLowerCase().includes(searchLc) ||
      (p.article || '').toLowerCase().includes(searchLc)
    )
  );

  // «Окно» списка: рисуем не все товары сразу (при 1000+ это тормозит переходы
  // и кнопку «назад»), а порцию, и дорисовываем при прокрутке вниз.
  const PAGE = 60;
  const [visible, setVisible] = useState(PAGE);
  useEffect(() => { setVisible(PAGE); }, [search, quickFilter, items.length]);
  const shown = filtered.slice(0, visible);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(v => (v < filtered.length ? v + PAGE : v));
      }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length, visible]);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  if (editing) {
    return <ProductForm
      product={editing === 'new' ? null : editing}
      cats={cats}
      allProducts={items}
      onCancel={() => setEditing(null)}
      onSaved={() => { setEditing(null); load(); show(t('common.success')); }}
      onError={(m) => show(m, 'error')}
    />;
  }

  return (
    <div className="page">
      {ToastView}
      {PinModal}
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t("products.titleCount", { count: items.length })}</p>
          <h1 className="page-title">{t('products.title')}</h1>
        </div>
        {isModuleOn(shop, 'categories') && (
          <button
            onClick={() => navigate('/categories')}
            aria-label={t('categories.title')}
            style={{
              width: 40, height: 40,
              borderRadius: 'var(--r-sm)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              color: 'var(--fg-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              marginRight: 6,
            }}
          >
            <Icon name="folder" size={18} />
          </button>
        )}
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowActions(true)}
          style={{ width: 'auto', padding: '8px 14px' }}
        >
          <Icon name="plus" size={14} stroke={2.5} />
          {t('products.actionButton')}
        </button>
      </div>

      <div style={{ padding: '0 20px 12px', display: 'flex', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          flex: 1,
          height: 48, padding: '0 16px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
        }}>
          <Icon name="search" size={18} color="var(--fg-faint)" />
          <input
            placeholder={t("products.search")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              all: 'unset', flex: 1, fontSize: 15,
              fontFamily: 'inherit', color: 'var(--fg)',
            }}
          />
        </div>
        {(hasFilterItems || quickFilter) && (
          <button
            onClick={() => setFiltersOpen(o => !o)}
            aria-label={t('products.filter', 'Фильтр')}
            style={{
              width: 48, height: 48, flexShrink: 0,
              borderRadius: 'var(--r-md)',
              background: quickFilter ? 'var(--brand-primary)' : 'var(--bg-elevated)',
              border: `1px solid ${quickFilter ? 'var(--brand-primary)' : 'var(--border)'}`,
              color: quickFilter ? '#fff' : 'var(--fg-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Icon name="filter" size={20} />
          </button>
        )}
        <ScanButton onScan={handleScan} size="lg" />
      </div>

      {(filtersOpen || quickFilter) && (
        <div style={{ padding: '0 20px 12px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            ['noPrice',    t('products.filterNoPrice', 'Без цены')],
            ['noCost',     t('products.filterNoCost', 'Без закупочной цены')],
            ['noBarcode',  t('products.filterNoBarcode', 'Без штрихкода')],
            ['genBarcode', t('products.filterGenBarcode', 'Свой штрихкод')],
          ].filter(([k]) => filterCounts[k] > 0 || quickFilter === k).map(([k, label]) => {
            const on = quickFilter === k;
            return (
              <button key={k}
                onClick={() => setQuickFilter(on ? null : k)}
                style={{
                  padding: '8px 12px', borderRadius: 'var(--r-full)',
                  background: on ? 'var(--brand-primary)' : 'var(--bg-elevated)',
                  border: `1px solid ${on ? 'var(--brand-primary)' : 'var(--border)'}`,
                  color: on ? '#fff' : 'var(--fg)',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                {label} · {filterCounts[k]}
              </button>
            );
          })}
          {quickFilter && (
            <button
              onClick={() => { setQuickFilter(null); setFiltersOpen(false); }}
              style={{
                padding: '8px 12px', borderRadius: 'var(--r-full)',
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--fg-muted)', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
              <Icon name="x" size={14} /> {t('products.filterReset', 'Все товары')}
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><Icon name={quickFilter && !searchLc ? 'check' : 'package'} size={28} stroke={1.5} /></div>
          <p>{quickFilter && !searchLc
            ? t('products.filterAllDone', 'Всё заполнено')
            : (search ? t('products.noProductsFound') : t('products.noProducts'))}</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map(p => (
            <div key={p.id} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12 }}>
              {getMainPhoto(p) ? (
                <img src={getMainPhoto(p)} alt="" loading="lazy" decoding="async" style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: 'var(--r-sm)',
                  background: 'var(--bg-subtle)',
                  color: 'var(--fg-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}><Icon name="package" size={20} /></div>
              )}
              <div style={{ flex: 1, minWidth: 0, opacity: p.showInCatalog === false ? 0.55 : 1 }}>
                <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {p.showInCatalog === false && <Icon name="eyeOff" size={12} color="var(--fg-faint)" />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  {p.isService && (
                    <span style={{
                      fontSize: 9, fontWeight: 700,
                      background: 'var(--brand-accent-soft, var(--brand-primary-soft))',
                      color: 'var(--brand-accent)',
                      padding: '1px 5px', borderRadius: 4, flexShrink: 0,
                    }}>
                      {t('products.serviceTag')}
                    </span>
                  )}
                  {Array.isArray(p.variants) && p.variants.filter(v => v.active !== false).length > 1 && (
                    <span style={{
                      fontSize: 9, fontWeight: 700,
                      background: 'var(--brand-accent-soft, var(--brand-primary-soft))',
                      color: 'var(--brand-accent)',
                      padding: '1px 5px', borderRadius: 4,
                      flexShrink: 0,
                    }}>
                      {t('variants.variantsCount', { count: p.variants.filter(v => v.active !== false).length })}
                    </span>
                  )}
                </p>
                <p style={{ fontSize: 12, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(p.salePrice || 0, currency)}
                  {!p.isService && (<>
                    {' · '}
                    {isComposite(p)
                      ? t('products.canAssemble', { count: computeAvailable(p, items) })
                      : `${p.stock} ${unitLabel(p.unit)}`}
                  </>)}
                </p>
              </div>
              <button onClick={() => setSharing(p)} style={smallIcon} aria-label={t('shareProduct.share')}><Icon name="share" size={14} /></button>
              <button onClick={() => setEditing(p)} style={smallIcon}><Icon name="edit" size={14} /></button>
              <button onClick={() => remove(p)} style={smallIcon}><Icon name="trash" size={14} /></button>
            </div>
          ))}
          {visible < filtered.length && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}
        </div>
      )}

      {showActions && (
        <ActionMenu
          onClose={() => setShowActions(false)}
          onPick={(action) => {
            setShowActions(false);
            if (action === 'new') setEditing('new');
            else if (action === 'service') setEditing({ _isService: true });
            else if (action === 'inventory') navigate('/inventory');
            else if (action === 'wholesale') navigate('/wholesale-sale');
            else if (action === 'printCatalog') navigate('/print-catalog');
            else setOpType(action);
          }}
        />
      )}

      {opType && (
        <StockOpSheet
          type={opType}
          products={items}
          onClose={() => setOpType(null)}
          onSaved={() => { setOpType(null); load(); show(t('common.success')); }}
          onError={(m) => show(m, 'error')}
        />
      )}

      {sharing && (
        <ShareProductDialog
          product={sharing}
          shop={shop}
          currency={currency}
          onClose={() => setSharing(null)}
          onError={() => show(t('shareProduct.shareError'), 'error')}
          onSuccess={(res) => {
            if (res?.method === 'download-fallback') {
              show(t('shareProduct.downloadedAndCopied'), 'success');
            }
          }}
        />
      )}

      <BottomNav items={navItems} />
    </div>
  );
}

const smallIcon = {
  width: 32, height: 32, background: 'var(--bg-subtle)',
  border: 'none', borderRadius: 'var(--r-xs)', cursor: 'pointer',
  color: 'var(--fg-muted)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

// ─── Меню выбора действия ─────────────────────────────────────
function ActionMenu({ onClose, onPick }) {
  const { t } = useTranslation();
  const shop = useMyShop();
  const { isOwner, can } = useAuth();
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <div className="modal-header">
          <p className="modal-title">{t('products.actionMenuTitle')}</p>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ActionRow
            iconName="plus"
            iconColor="var(--brand-primary)"
            iconBg="var(--brand-primary-soft)"
            title={t('products.newProduct')}
            desc={t('products.newProductDesc')}
            onClick={() => onPick('new')}
          />
          {canSeeModule(shop, 'services', { isOwner, can }) && (
            <ActionRow
              iconName="sparkles"
              iconColor="var(--brand-accent)"
              iconBg="var(--brand-accent-soft)"
              title={t('products.newService')}
              desc={t('products.newServiceDesc')}
              onClick={() => onPick('service')}
            />
          )}
          {canSeeModule(shop, 'purchases', { isOwner, can }) && (
            <ActionRow
              iconName="package"
              iconColor="var(--brand-accent)"
              iconBg="var(--brand-accent-soft)"
              title={t('products.purchase')}
              desc={t('products.purchaseDesc')}
              onClick={() => onPick('purchase')}
            />
          )}
          {canSeeModule(shop, 'returns', { isOwner, can }) && (
            <ActionRow
              iconName="refresh"
              iconColor="var(--warning)"
              iconBg="var(--warning-soft)"
              title={t('products.return')}
              desc={t('products.returnDesc')}
              onClick={() => onPick('return')}
            />
          )}
          {canSeeModule(shop, 'writeoffs', { isOwner, can }) && (
            <ActionRow
              iconName="trash"
              iconColor="var(--danger)"
              iconBg="var(--danger-soft)"
              title={t('products.writeoff')}
              desc={t('products.writeoffDesc')}
              onClick={() => onPick('writeoff')}
            />
          )}
          {canSeeModule(shop, 'wholesale', { isOwner, can }) && (
            <ActionRow
              iconName="truck"
              iconColor="var(--success)"
              iconBg="var(--success-soft)"
              title={t('products.giveToWholesale')}
              desc={t('products.giveToWholesaleDesc')}
              onClick={() => onPick('wholesale')}
            />
          )}
          {canSeeModule(shop, 'ownBarcodes', { isOwner, can }) && (
            <ActionRow
              iconName="fileText"
              iconColor="var(--brand-primary)"
              iconBg="var(--brand-primary-soft)"
              title={t('catalog.printTitle', 'Бумажный каталог')}
              desc={t('catalog.printDesc', 'PDF с фото и штрихкодами — распечатать и сканировать')}
              onClick={() => onPick('printCatalog')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ActionRow({ iconName, iconColor, iconBg, title, desc, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 14,
      background: 'var(--bg-subtle)', border: 'none',
      borderRadius: 'var(--r-md)',
      cursor: 'pointer', textAlign: 'left',
      fontFamily: 'inherit', color: 'inherit',
      transition: 'transform 120ms var(--ease-spring)',
    }}
      onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
      onTouchEnd={(e) => e.currentTarget.style.transform = ''}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
      onMouseUp={(e) => e.currentTarget.style.transform = ''}
      onMouseLeave={(e) => e.currentTarget.style.transform = ''}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 'var(--r-sm)',
        background: iconBg, color: iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={iconName} size={22} stroke={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{title}</p>
        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{desc}</p>
      </div>
      <Icon name="chevronRight" size={16} color="var(--fg-faint)" stroke={2.5} />
    </button>
  );
}

// ─── Sheet операции (закупка/возврат/списание) ────────────────
function StockOpSheet({ type, products, onClose, onSaved, onError }) {
  const { t } = useTranslation();
  const currency = useMyCurrency();
  const [step, setStep]       = useState('pickProduct'); // pickProduct → pickVariant → fillForm
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [search, setSearch]   = useState('');
  const [qty, setQty]         = useState('');
  const [money, setMoney]     = useState('');
  const reasonRef             = useRef();
  const [moneyEdited, setMoneyEdited] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [activeField, setActiveField] = useState('qty'); // 'qty' | 'money'

  // Поставщик — только для type='purchase'
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers]   = useState([]);

  // Подгрузим список поставщиков (если это закупка и есть доступ)
  useEffect(() => {
    if (type !== 'purchase') return;
    let alive = true;
    import('../../api').then(({ counterparties: cpApi }) => {
      cpApi.list({ type: 'supplier' })
        .then(r => { if (alive) setSuppliers(r.items || []); })
        .catch(() => {});
    });
    return () => { alive = false; };
  }, [type]);

  // Когда выбрали товар у которого есть defaultSupplierId — предзаполнить
  useEffect(() => {
    if (type !== 'purchase') return;
    if (product?.defaultSupplierId) setSupplierId(product.defaultSupplierId);
    else setSupplierId('');
  }, [product, type]);

  const titleByType = {
    purchase: t('products.purchase'),
    return:   t('products.return'),
    writeoff: t('products.writeoff'),
  }[type];

  const moneyHintByType = {
    purchase: t('stockOp.purchaseAmount'),
    return:   t('stockOp.returnAmount'),
    writeoff: t('stockOp.writeoffLoss'),
  }[type];

  const reasonHintByType = {
    purchase: t('stockOp.purchaseHint'),
    return:   t('stockOp.returnHint'),
    writeoff: t('stockOp.writeoffHint'),
  }[type];

  const confirmByType = {
    purchase: t('stockOp.confirmPurchase'),
    return:   t('stockOp.confirmReturn'),
    writeoff: t('stockOp.confirmWriteoff'),
  }[type];

  const productActiveVariants = (p) => {
    if (!Array.isArray(p?.variants)) return [];
    return p.variants.filter(v => v.active !== false);
  };

  // Шаг 1: товар выбран
  const onPickProduct = (p) => {
    setProduct(p);
    const vars = productActiveVariants(p);
    if (vars.length > 1) {
      // 2+ вариантов — переходим к выбору варианта
      setVariant(null);
      setStep('pickVariant');
    } else {
      // Один или ноль вариантов — сразу к форме
      setVariant(vars[0] || null);
      setStep('fillForm');
      setQty('');
      setMoney('');
      setMoneyEdited(false);
      setActiveField('qty');
    }
  };

  // Сканирование в окне выбора товара для операции склада
  const handleStockOpScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      if (r?.product) {
        // Если есть конкретный вариант — сразу с ним
        if (r.variant) {
          setProduct(r.product);
          setVariant(r.variant);
          setStep('fillForm');
          setQty('');
          setMoney('');
          setMoneyEdited(false);
          setActiveField('qty');
          return;
        }
        // Иначе — как обычный пик
        onPickProduct(r.product);
        return;
      }
    } catch (e) {
      // Не найден или ошибка
    }
    onError(t('products.barcodeNotFound', 'Товар с таким баркодом не найден'));
  };

  // Шаг 2: вариант выбран
  const onPickVariant = (v) => {
    setVariant(v);
    setStep('fillForm');
    setQty('');
    setMoney('');
    setMoneyEdited(false);
    setActiveField('qty');
  };

  // Эффективные цены — приоритет варианта
  const effectiveCostPrice = variant?.costPrice ?? product?.costPrice ?? 0;
  const effectiveSalePrice = variant?.salePrice ?? product?.salePrice ?? 0;

  // Авторасчёт суммы
  const onQtyChange = (v) => {
    setQty(v);
    if (!moneyEdited && product) {
      const qtyNum = parseQty(v);
      if (qtyNum) {
        const auto = type === 'writeoff'
          ? qtyNum * (effectiveCostPrice || 0)
          : type === 'purchase'
          ? qtyNum * (effectiveCostPrice || 0)
          : qtyNum * (effectiveSalePrice || 0);
        setMoney(String(auto.toFixed(2)).replace('.', ',').replace(/,0+$/, ''));
      } else {
        setMoney('');
      }
    }
  };

  const onMoneyChange = (v) => {
    setMoney(v);
    setMoneyEdited(true);
  };

  const filteredProducts = products.filter(p =>
    !search.trim() ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.article || '').toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    const qtyNum = parseQty(qty);
    if (!qtyNum) return onError(t('stockOp.errorQty'));
    const moneyNum = Math.max(0, Number(String(money).replace(',', '.')) || 0);

    setSaving(true);
    try {
      await stockOpsApi.create({
        productId: product.id,
        variantId: variant?.id || null,
        type,
        qty: qtyNum,
        money: moneyNum,
        reason: reasonRef.current?.value || '',
        supplierId: type === 'purchase' ? (supplierId || null) : null,
      });
      onSaved();
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  // Парсинг для отображения
  const qtyNum = parseQty(qty) || 0;
  const moneyDisplay = money || '0';

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet" style={{ maxHeight: '92vh' }}>
        <div className="modal-header">
          <p className="modal-title">{titleByType}</p>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        {step === 'pickProduct' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 12 }}>
              {t("stockOp.pickProduct")}
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                flex: 1,
                height: 44, padding: '0 14px',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
              }}>
                <Icon name="search" size={16} color="var(--fg-faint)" />
                <input
                  placeholder={t("products.search")}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    all: 'unset', flex: 1, fontSize: 14,
                    fontFamily: 'inherit', color: 'var(--fg)',
                  }}
                  autoFocus
                />
              </div>
              <ScanButton onScan={handleStockOpScan} size="md" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
              {filteredProducts.length === 0 ? (
                <p style={{ padding: 20, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 13 }}>
                  {t('products.noProductsFound')}
                </p>
              ) : filteredProducts.map(p => (
                <button
                  key={p.id}
                  onClick={() => onPickProduct(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: 10, background: 'var(--bg-subtle)',
                    border: 'none', borderRadius: 'var(--r-sm)',
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  }}
                >
                  {getMainPhoto(p) ? (
                    <img src={getMainPhoto(p)} alt="" loading="lazy" decoding="async" style={{ width: 36, height: 36, borderRadius: 'var(--r-xs)', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 36, height: 36, borderRadius: 'var(--r-xs)',
                      background: 'var(--bg-elevated)', color: 'var(--fg-faint)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}><Icon name="package" size={16} /></div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                      {t("stockOp.stockLeft", { qty: p.stock, unit: unitLabel(p.unit) })}
                    </p>
                  </div>
                  <Icon name="chevronRight" size={14} color="var(--fg-faint)" />
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'pickVariant' && product && (
          <>
            {/* Шапка с выбранным товаром */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: 10, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)' }}>
              {getMainPhoto(product) ? (
                <img src={getMainPhoto(product)} alt="" style={{ width: 40, height: 40, borderRadius: 'var(--r-xs)', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: 40, height: 40, borderRadius: 'var(--r-xs)',
                  background: 'var(--bg-elevated)', color: 'var(--fg-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}><Icon name="package" size={18} /></div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
                <p style={{ fontSize: 11, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {t('variants.variantsCount', { count: productActiveVariants(product).length })}
                </p>
              </div>
              <button
                onClick={() => { setStep('pickProduct'); setProduct(null); setVariant(null); }}
                style={{
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-xs)', padding: '6px 10px',
                  fontSize: 11, fontWeight: 600, color: 'var(--fg-muted)',
                  cursor: 'pointer', fontFamily: 'var(--font-display)',
                }}
              >
                {t('stockOp.change')}
              </button>
            </div>

            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 10 }}>
              {t('variants.pickVariant')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
              {productActiveVariants(product).map(v => {
                const variantPhoto = (Array.isArray(v.photos) && v.photos[0]) || getMainPhoto(product);
                const attrLine = v.attributeValues
                  ? Object.values(v.attributeValues).filter(Boolean).join(' / ')
                  : '';
                return (
                  <button
                    key={v.id}
                    onClick={() => onPickVariant(v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: 10, background: 'var(--bg-subtle)',
                      border: 'none', borderRadius: 'var(--r-sm)',
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                    }}
                  >
                    {variantPhoto ? (
                      <img src={variantPhoto} alt="" style={{ width: 36, height: 36, borderRadius: 'var(--r-xs)', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{
                        width: 36, height: 36, borderRadius: 'var(--r-xs)',
                        background: 'var(--bg-elevated)', color: 'var(--fg-faint)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}><Icon name="package" size={16} /></div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attrLine || v.name || t('variants.defaultVariant')}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(v.salePrice || 0, currency)} · {t('stockOp.stockLeft', { qty: v.stock || 0, unit: unitLabel(product.unit) })}
                        {v.sku && <> · {v.sku}</>}
                      </p>
                    </div>
                    <Icon name="chevronRight" size={14} color="var(--fg-faint)" />
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 'fillForm' && product && (
          <>
            {/* Шапка с выбранным товаром (и вариантом если есть) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: 10, background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)' }}>
              {((variant && Array.isArray(variant.photos) && variant.photos[0]) || getMainPhoto(product)) ? (
                <img
                  src={(variant && Array.isArray(variant.photos) && variant.photos[0]) || getMainPhoto(product)}
                  alt=""
                  style={{ width: 40, height: 40, borderRadius: 'var(--r-xs)', objectFit: 'cover', flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 40, height: 40, borderRadius: 'var(--r-xs)',
                  background: 'var(--bg-elevated)', color: 'var(--fg-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}><Icon name="package" size={18} /></div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {product.name}
                  {variant && variant.attributeValues && Object.keys(variant.attributeValues).length > 0 && (
                    <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}>
                      {' · ' + Object.values(variant.attributeValues).filter(Boolean).join(' / ')}
                    </span>
                  )}
                </p>
                <p style={{ fontSize: 11, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {t("stockOp.stockLeft", { qty: variant ? (variant.stock || 0) : product.stock, unit: unitLabel(product.unit) })}
                </p>
              </div>
              <button
                onClick={() => {
                  // Если у товара несколько вариантов — назад к выбору варианта
                  // иначе к выбору товара
                  if (productActiveVariants(product).length > 1) {
                    setStep('pickVariant');
                    setVariant(null);
                  } else {
                    setStep('pickProduct');
                    setProduct(null);
                    setVariant(null);
                  }
                }}
                style={{
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-xs)', padding: '6px 10px',
                  fontSize: 11, fontWeight: 600, color: 'var(--fg-muted)',
                  cursor: 'pointer', fontFamily: 'var(--font-display)',
                }}
              >
                {t('stockOp.change')}
              </button>
            </div>

            {/* Два поля бок о бок: количество и сумма (можно переключать) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <FieldTile
                active={activeField === 'qty'}
                onClick={() => setActiveField('qty')}
                label={t("stockOp.quantity")}
                value={qty}
                placeholder="0"
                suffix={unitLabel(product.unit)}
              />
              <FieldTile
                active={activeField === 'money'}
                onClick={() => setActiveField('money')}
                label={moneyHintByType}
                value={money}
                placeholder="0"
                suffix={getCurrencySymbol(currency)}
                edited={moneyEdited}
              />
            </div>

            {/* Числовая клавиатура — пишет в активное поле */}
            <NumPad
              value={activeField === 'qty' ? qty : money}
              onChange={(v) => activeField === 'qty' ? onQtyChange(v) : onMoneyChange(v)}
              allowDecimal={true}
            />

            {/* Поле причины */}
            <div className="input-group" style={{ marginTop: 14, marginBottom: 12 }}>
              <label>{type === "purchase" ? t("stockOp.comment") : t("stockOp.commentRecommended")}</label>
              <input
                ref={reasonRef}
                className="input"
                placeholder={reasonHintByType}
                style={{ fontSize: 14 }}
              />
            </div>

            {/* Поставщик — только для закупки */}
            {type === 'purchase' && suppliers.length > 0 && (
              <div className="input-group" style={{ marginBottom: 12 }}>
                <label>{t('stockOp.supplier')}</label>
                <select
                  className="select"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <option value="">— {t('common.notSet')} —</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="btn btn-primary btn-lg"
              onClick={save}
              disabled={saving || qtyNum === 0}
              style={{ width: '100%' }}
            >
              {saving ? t("common.saving") : confirmByType}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FieldTile({ active, onClick, label, value, placeholder, suffix, edited }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
        border: '2px solid', borderColor: active ? 'var(--brand-primary)' : 'transparent',
        borderRadius: 'var(--r-sm)',
        padding: 12,
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        minWidth: 0,
      }}
    >
      <p style={{ fontSize: 10, fontWeight: 700, color: active ? 'var(--brand-primary)' : 'var(--fg-muted)', letterSpacing: '0.06em' }}>
        {label}{edited && ' ✏️'}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        <span style={{
          fontSize: 22, fontWeight: 800,
          color: value ? 'var(--fg-strong)' : 'var(--fg-faint)',
          fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)',
          flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {value || placeholder}
        </span>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}>{suffix}</span>
      </div>
    </button>
  );
}

// ─── Форма создания/редактирования товара ─────────────────────
function ProductForm({ product, cats, allProducts = [], onCancel, onSaved, onError }) {
  const { t } = useTranslation();
  const { confirm: confirmDeleteVariant, PinModal: VariantPinModal } = useDeletePin();
  const currency = useMyCurrency();
  const myShop = useMyShop();
  const nameRef = useRef();
  const descRef = useRef();
  const articleRef = useRef();
  const costRef = useRef();
  const priceRef = useRef();
  const stockRef = useRef();
  // photos — массив URL. Поддерживаем оба формата: новый product.photos[] и старый product.photo.
  const initialPhotos = (() => {
    if (Array.isArray(product?.photos) && product.photos.length > 0) return product.photos;
    if (product?.photo) return [product.photo];
    return [];
  })();
  const [photos, setPhotos] = useState(initialPhotos);

  // AI генерация — открываем модалку и при успехе получаем outputUrl
  const [aiOpen, setAiOpen] = useState(false);

  const [unit, setUnit] = useState(product?.unit || 'шт');
  const [categoryId, setCategoryId] = useState(product?.categoryId || '');
  const [showInCatalog, setShowInCatalog] = useState(product?.showInCatalog ?? true);
  const [defaultSupplierId, setDefaultSupplierId] = useState(product?.defaultSupplierId || '');
  const [barcode, setBarcode] = useState(product?.barcode || product?._newBarcode || '');
  const [markingCode, setMarkingCode] = useState(product?.markingCode || '');
  const [plu, setPlu] = useState(product?.plu != null ? String(product.plu) : '');
  // Услуга: без склада/закупок/маркировки/вариантов
  const isService = !!(product?.isService || product?._isService);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Если открылись со сканированным баркодом (новый товар через сканер из главного экрана),
  // пробуем подтянуть имя из Open Food Facts
  useEffect(() => {
    if (!product?._newBarcode) return;
    let alive = true;
    productsApi.lookupExternal(product._newBarcode)
      .then(r => {
        if (!alive) return;
        if (r?.found && r.name && nameRef.current && !nameRef.current.value) {
          nameRef.current.value = r.name;
        }
      })
      .catch(() => {});  // тихо
    return () => { alive = false; };
  }, []); // eslint-disable-line

  // Подгрузим список поставщиков (только если есть доступ — иначе тихо проигнорируем 403)
  useEffect(() => {
    let alive = true;
    import('../../api').then(({ counterparties: cpApi }) => {
      cpApi.list({ type: 'supplier' })
        .then(r => { if (alive) setSuppliers(r.items || []); })
        .catch(() => {});  // у сотрудника нет доступа — это норм, dropdown не покажется
    });
    return () => { alive = false; };
  }, []);

  // Состав: массив { componentId, qty, _component? }
  // _component — закешированные данные о компоненте для отображения и расчёта (имя, unit, costPrice)
  const [components, setComponents] = useState(() => {
    if (!product?.components) return [];
    return product.components.map(c => ({
      componentId: c.componentId || c.component?.id,
      qty: c.qty,
      _component: c.component || null,
    }));
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Ручное переопределение себестоимости составного товара
  const [costOverride, setCostOverride] = useState(product?.costPriceOverride != null);

  // ─── Варианты товара (matrix) ────────────────────────────────
  // attrs: [{ name: 'Размер', values: ['S','M','L'] }, ...] — массив до 3 атрибутов
  // variants: [{ id?, name, sku, attributeValues: { Размер: 'S', Цвет: 'чёрный' }, salePrice, costPrice, stock, photos, isDefault, _new?, _deleted? }]
  const initialAttrs = (() => {
    if (!Array.isArray(product?.attributes) || product.attributes.length === 0) return [];
    return product.attributes.map(name => ({ name, values: [] }));
  })();
  // Если есть варианты — заполним values из них (уникальные значения по каждому атрибуту)
  const initialAttrsFilled = (() => {
    if (initialAttrs.length === 0) return [];
    const out = initialAttrs.map(a => ({ name: a.name, values: [] }));
    const allVariants = product?.variants || [];
    for (const v of allVariants) {
      if (!v.attributeValues) continue;
      for (let i = 0; i < out.length; i++) {
        const val = v.attributeValues[out[i].name];
        if (val && !out[i].values.includes(val)) out[i].values.push(val);
      }
    }
    return out;
  })();
  const [attrs, setAttrs] = useState(initialAttrsFilled);
  // Включён ли режим вариантов
  const initialHasVariants =
    Array.isArray(product?.variants) &&
    product.variants.filter(v => v.active !== false).length > 1;
  const [hasVariants, setHasVariants] = useState(initialHasVariants);
  // Текущий список вариантов
  const [variants, setVariants] = useState(() => {
    if (!Array.isArray(product?.variants)) return [];
    return product.variants
      .filter(v => v.active !== false)
      .map(v => ({
        id: v.id,
        name: v.name || '',
        sku: v.sku || '',
        barcode: v.barcode || '',
        markingCode: v.markingCode || '',
        attributeValues: v.attributeValues || {},
        salePrice: v.salePrice ?? 0,
        costPrice: v.costPrice ?? 0,
        stock: v.stock ?? 0,
        photos: Array.isArray(v.photos) ? v.photos : [],
        isDefault: !!v.isDefault,
      }));
  });
  const [editingVariantIdx, setEditingVariantIdx] = useState(null);

  const [catSheetOpen, setCatSheetOpen] = useState(false);
  // Товар с таким же штрихкодом, который УЖЕ есть в каталоге
  const [dupProduct, setDupProduct] = useState(null);

  // Сгенерировать внутренний штрихкод (EAN-13, префикс 20) для товара
  // без заводского кода. Уникальность проверяем по каталогу.
  // Подобрать наименьший свободный номер на весах (PLU) по текущему каталогу
  const suggestFreePlu = () => {
    // следующий по порядку: максимальный занятый + 1 (есть 17 → 18; нет — 1)
    const used = (allProducts || [])
      .filter(p => p.id !== product?.id && p.plu != null)
      .map(p => Number(p.plu));
    const max = used.length ? Math.max(...used) : 0;
    setPlu(String(Math.min(max + 1, 99999)));
  };

  const handleGenerateBarcode = () => {
    // Мгновенно, без сети: генерим локально и сразу вставляем в поле.
    // Уникальность проверяем по уже загруженным товарам магазина —
    // этого достаточно (пространство кодов огромно, коллизия ничтожна).
    const taken = new Set(
      (allProducts || []).map(p => String(p.barcode || '')).filter(Boolean)
    );
    const code = generateBarcodeSync(taken);
    setBarcode(code);
    setDupProduct(null);
  };

  // Один штрихкод = один товар. Иначе касса при сканировании не знает,
  // какой из дублей пробивать.
  const checkBarcodeDuplicate = async (code) => {
    const c = String(code || '').trim();
    if (!c) { setDupProduct(null); return; }
    try {
      const r = await productsApi.byBarcode(c);
      const found = r?.product;
      // Не считаем дубликатом сам редактируемый товар
      if (found && found.id !== product?.id) setDupProduct(found);
      else setDupProduct(null);
    } catch (e) {
      setDupProduct(null);   // не нашли — значит дубликата нет
    }
  };


  const rootCats = cats.filter(c => !c.parentId);
  const subCats = cats.filter(c => c.parentId);
  const groupedCats = rootCats.map(rc => ({
    ...rc,
    children: subCats.filter(s => s.parentId === rc.id),
  }));

  // Расчётная себестоимость = Σ qty × component.costPrice
  const calculatedCost = components.reduce((sum, c) => {
    const cp = Number(c._component?.costPrice) || 0;
    return sum + cp * Number(c.qty || 0);
  }, 0);

  const addComponent = (productObj) => {
    setComponents(prev => [...prev, {
      componentId: productObj.id,
      qty: 1,
      _component: productObj,
    }]);
    setPickerOpen(false);
  };

  const updateComponentQty = (idx, qty) => {
    setComponents(prev => prev.map((c, i) => i === idx ? { ...c, qty } : c));
  };

  const removeComponent = (idx) => {
    setComponents(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Хелперы вариантов ───────────────────────────────────────

  // Картезианское произведение значений атрибутов
  const cartesian = (arrays) => {
    if (arrays.length === 0) return [];
    return arrays.reduce(
      (acc, curr) => acc.flatMap(a => curr.map(c => [...a, c])),
      [[]]
    );
  };

  // Атрибуты
  const addAttribute = () => {
    if (attrs.length >= 3) return;
    setAttrs(prev => [...prev, { name: '', values: [] }]);
  };
  const removeAttribute = (idx) => {
    setAttrs(prev => prev.filter((_, i) => i !== idx));
  };
  const updateAttrName = (idx, name) => {
    setAttrs(prev => prev.map((a, i) => i === idx ? { ...a, name } : a));
  };
  const updateAttrValues = (idx, valuesStr) => {
    const vals = String(valuesStr || '')
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
    setAttrs(prev => prev.map((a, i) => i === idx ? { ...a, values: vals, _raw: undefined } : a));
  };

  // Сохраняем сырой текст в state пока юзер печатает (чтобы запятая и пробелы не съедались)
  // На onBlur парсим в values массив.
  const updateAttrValuesRaw = (idx, raw) => {
    setAttrs(prev => prev.map((a, i) => {
      if (i !== idx) return a;
      const vals = String(raw || '').split(',').map(v => v.trim()).filter(Boolean);
      return { ...a, values: vals, _raw: raw };
    }));
  };

  // Сгенерировать (или пересобрать) матрицу вариантов из текущих атрибутов.
  // Если есть существующие варианты с теми же значениями атрибутов — сохраняем их данные.
  const generateMatrix = () => {
    const cleanAttrs = attrs
      .map(a => ({ name: String(a.name || '').trim(), values: (a.values || []).filter(Boolean) }))
      .filter(a => a.name && a.values.length > 0);

    if (cleanAttrs.length === 0) {
      onError(t('variants.minimumOneValue'));
      return;
    }

    // Базовые значения для новых вариантов = общие поля товара
    const baseSale = parseFloat(priceRef.current?.value || product?.salePrice || '0') || 0;
    const baseCost = parseFloat(costRef.current?.value || product?.costPrice || '0') || 0;
    const baseStock = parseFloat(stockRef.current?.value || product?.stock || '0') || 0;

    const combos = cartesian(cleanAttrs.map(a => a.values));
    const newVariants = combos.map((combo, idx) => {
      const attributeValues = {};
      cleanAttrs.forEach((a, i) => { attributeValues[a.name] = combo[i]; });
      const displayName = combo.join(' / ');
      // Ищем существующий с теми же атрибутами — сохраняем его данные (цена/остаток)
      const existing = variants.find(v =>
        cleanAttrs.every(a => v.attributeValues?.[a.name] === attributeValues[a.name])
      );
      if (existing) {
        return { ...existing, attributeValues, name: displayName };
      }
      return {
        name: displayName,
        sku: '',
        barcode: '',
        attributeValues,
        salePrice: baseSale,
        costPrice: baseCost,
        stock: baseStock,
        photos: [],
        isDefault: idx === 0,
      };
    });
    setVariants(newVariants);
  };

  // Добавить пустой вариант вручную
  const addEmptyVariant = () => {
    const baseSale = parseFloat(priceRef.current?.value || product?.salePrice || '0') || 0;
    const baseCost = parseFloat(costRef.current?.value || product?.costPrice || '0') || 0;
    const baseStock = parseFloat(stockRef.current?.value || product?.stock || '0') || 0;
    const cleanAttrNames = attrs.map(a => String(a.name || '').trim()).filter(Boolean);
    const attributeValues = {};
    cleanAttrNames.forEach(n => { attributeValues[n] = ''; });
    setVariants(prev => [...prev, {
      name: '',
      sku: '',
      barcode: '',
      attributeValues,
      salePrice: baseSale,
      costPrice: baseCost,
      stock: baseStock,
      photos: [],
      isDefault: prev.length === 0,
    }]);
    setEditingVariantIdx(variants.length);
  };

  const updateVariant = (idx, patch) => {
    setVariants(prev => prev.map((v, i) => i === idx ? { ...v, ...patch } : v));
  };

  const removeVariant = async (idx) => {
    const v = variants[idx];
    const ok = await confirmDeleteVariant({
      title: t('variants.deleteTitle', 'Удалить вариант?'),
      message: t('variants.confirmRemoveVariant', { name: v.name || v.sku || `#${idx + 1}` }),
    });
    if (!ok) return;
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    const name = nameRef.current?.value?.trim();
    if (!name) return onError(t('products.errorNameRequired'));
    const salePrice = parseFloat(priceRef.current?.value || '0') || 0;
    // Цена необязательна: пустая сохранится как 0.

    // Если включён режим вариантов — нужны атрибуты и варианты
    if (hasVariants) {
      const cleanAttrs = attrs
        .map(a => ({ name: String(a.name || '').trim(), values: a.values.filter(Boolean) }))
        .filter(a => a.name && a.values.length > 0);
      if (cleanAttrs.length === 0) return onError(t('variants.minimumOneValue'));
      if (variants.length === 0) return onError(t('variants.noVariants'));
    }

    setSaving(true);

    // Если есть компоненты И нет ручного переопределения — costPrice берём расчётную
    const manualCost = parseFloat(costRef.current?.value || '0') || 0;
    const finalCostPrice = (components.length > 0 && !costOverride) ? calculatedCost : manualCost;

    // Атрибуты товара
    const cleanAttrNames = hasVariants
      ? attrs.map(a => String(a.name || '').trim()).filter(Boolean).slice(0, 3)
      : [];

    const data = {
      name,
      description: descRef.current?.value?.trim() || null,
      article: articleRef.current?.value?.trim() || null,
      isService,
      barcode: isService ? null : (barcode.trim() || null),
      markingCode: isService ? null : (markingCode.trim() || null),
      plu: isService ? null : (plu.trim() === '' ? null : parseInt(plu, 10)),
      unit,
      costPrice: finalCostPrice,
      salePrice,
      photos,
      photo: photos[0] || null,
      categoryId: categoryId || null,
      showInCatalog,
      defaultSupplierId: defaultSupplierId || null,
      attributes: cleanAttrNames,
      components: components.map(c => ({
        componentId: c.componentId,
        qty: Number(c.qty) || 0,
      })).filter(c => c.componentId && c.qty > 0),
      costPriceOverride: (components.length > 0 && costOverride) ? manualCost : null,
    };

    // stock редактируется только у не-составных и не-вариантных товаров
    if (components.length === 0 && !hasVariants) {
      data.stock = parseFloat(stockRef.current?.value || '0') || 0;
    }

    try {
      // Сначала создаём/обновляем сам товар
      let savedProduct;
      if (product?.id) {
        // Если у товара уже несколько вариантов — НЕ отправляем salePrice/costPrice/stock,
        // бэк их отклоняет (см. PATCH /products/:id, "Меняйте цену и остаток через варианты")
        const existingVariantsCount = (product.variants || []).filter(v => v.active !== false).length;
        if (existingVariantsCount > 1 || hasVariants) {
          delete data.stock;
          delete data.salePrice;
          delete data.costPrice;
        }
        savedProduct = await productsApi.update(product.id, data);
      } else {
        savedProduct = await productsApi.create(data);
      }

      // Если режим вариантов — синхронизируем варианты
      if (hasVariants) {
        // Существующие варианты на сервере (из последнего ответа или загрузки)
        const serverVariants = savedProduct?.variants || [];
        const serverById = new Map(serverVariants.map(v => [v.id, v]));

        // Существующие в форме
        const formIds = new Set(variants.filter(v => v.id).map(v => v.id));

        // 1. Удаляем варианты которые были на сервере но больше не в форме
        for (const sv of serverVariants) {
          if (!sv.id) continue;
          if (sv.isDefault && variants.length === 0) continue; // нельзя удалить дефолтный
          if (!formIds.has(sv.id)) {
            try {
              await productsApi.variants.remove(savedProduct.id, sv.id);
            } catch (e) {
              // последний вариант нельзя удалить — игнорируем
            }
          }
        }

        // 2. Создаём новые / обновляем существующие
        for (const v of variants) {
          const payload = {
            name: v.name || '',
            sku: v.sku || null,
            barcode: v.barcode || null,
            markingCode: v.markingCode || null,
            attributeValues: v.attributeValues || {},
            salePrice: Number(v.salePrice) || 0,
            costPrice: Number(v.costPrice) || 0,
            stock: Number(v.stock) || 0,
            photos: Array.isArray(v.photos) ? v.photos : [],
          };
          if (v.id && serverById.has(v.id)) {
            await productsApi.variants.update(savedProduct.id, v.id, payload);
          } else {
            await productsApi.variants.create(savedProduct.id, payload);
          }
        }

        // 3. Если на сервере есть дефолтный вариант от старого товара (без атрибутов),
        // и его нет в новой матрице — удаляем
        const defaultVariant = serverVariants.find(sv => sv.isDefault && !sv.attributeValues || (sv.isDefault && Object.keys(sv.attributeValues || {}).length === 0));
        if (defaultVariant && !formIds.has(defaultVariant.id) && variants.length > 0) {
          try {
            await productsApi.variants.remove(savedProduct.id, defaultVariant.id);
          } catch (e) {
            // нельзя удалить — оставляем
          }
        }
      }

      onSaved();
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  return (
    <div className="page" style={{ padding: '20px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="back-btn" onClick={onCancel}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{isService
          ? (product?.id ? t('products.editService') : t('products.newService'))
          : (product?.id ? t('products.editProduct') : t('products.addProduct'))}</h2>
      </div>

      <PhotoUploader
        photos={photos}
        onChange={setPhotos}
        onError={onError}
        uploadFn={uploadApi.photos}
      />

      {/* Кнопка AI генерации фото — только при включённом модуле AI */}
      {isModuleOn(myShop, 'ai') && (
      <button
        type="button"
        onClick={() => setAiOpen(true)}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 14px',
          marginTop: -4, marginBottom: 12,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--r-md, 12px)',
          fontSize: 14, fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Icon name="sparkles" size={16} />
        {t('aiGenerate.openBtn')}
      </button>
      )}

      {isModuleOn(myShop, 'ai') && (
      <AiGenerateSheet
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        existingPhotos={photos}
        defaultDescription={nameRef.current?.value || product?.name || ''}
        onApply={(url) => {
          // Заменяет ВСЕ фото на это (становится единственным главным)
          setPhotos([url, ...photos.filter(p => p !== url)]);
          setAiOpen(false);
        }}
        onAddToGallery={(url) => {
          // Добавляет в конец, главное фото не меняется
          if (!photos.includes(url)) setPhotos([...photos, url]);
          setAiOpen(false);
        }}
      />
      )}

      <div className="input-group">
        <label>{t("products.name")} *</label>
        <input ref={nameRef} className="input" defaultValue={product?.name || ''} placeholder={t("products.namePlaceholder")} />
      </div>

      <div className="input-group">
        <label>{t('products.description')}</label>
        <textarea
          ref={descRef}
          className="textarea"
          defaultValue={product?.description || ''}
          placeholder={t("products.descriptionPlaceholder")}
          style={{ minHeight: 80 }}
        />
      </div>

      <div className="input-group">
        <label>{t('products.article')}</label>
        <input ref={articleRef} className="input" defaultValue={product?.article || ''} placeholder={t("products.articlePlaceholder")} />
      </div>

      {!isService && (<>
      <div className="input-group">
        <label>{t('products.barcode', 'Штрихкод')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {isModuleOn(myShop, 'ownBarcodes') && (
            <button
              type="button"
              onClick={handleGenerateBarcode}
              title={t('products.generateBarcode', 'Сгенерировать штрихкод')}
              aria-label={t('products.generateBarcode', 'Сгенерировать штрихкод')}
              className="btn btn-secondary"
              style={{ flexShrink: 0, width: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon name="sparkles" size={18} />
            </button>
          )}
          <input
            className="input"
            style={{ flex: 1, minWidth: 0 }}
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onBlur={(e) => checkBarcodeDuplicate(e.target.value)}
            placeholder={t('products.barcodePlaceholder', 'EAN или GTIN, можно сканировать')}
            inputMode="numeric"
          />
          <ScanButton
            onScan={async (code) => {
              setBarcode(code);
              // Сначала — не завели ли уже такой товар
              checkBarcodeDuplicate(code);

              // Подтягиваем карточку по штрихкоду: сначала наш справочник, потом внешний.
              const currentName = (nameRef.current?.value || '').trim();
              if (!currentName) {
                try {
                  const r = await productsApi.lookupExternal(code);
                  if (r?.found && r.name && nameRef.current) {
                    nameRef.current.value = r.name;
                    onError && onError(null);

                    // ФОТО — только из НАШЕГО справочника (source === 'local').
                    // Внешние базы (Open Food Facts) регулярно отдают фото не того
                    // товара — например, Cola Light вместо классической. Неверное фото
                    // на витрине хуже, чем его отсутствие: ошибка уходит покупателю.
                    // Название из внешней базы берём — его человек сразу видит и поправит.
                    if (r.source === 'local' && r.image && photos.length === 0) {
                      setPhotos([r.image]);
                    }
                    if (r.source === 'local' && r.unit) setUnit(r.unit);
                  }
                } catch (e) {
                  // тихо игнорируем — это бонусная фича
                }
              }
            }}
            size="lg"
          />
        </div>

        {/* Такой штрихкод уже занят другим товаром */}
        {dupProduct && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', marginTop: 8,
            background: 'var(--warning-soft, #FEF3C7)',
            border: '1px solid var(--warning, #F59E0B)',
            borderRadius: 'var(--r-sm)',
          }}>
            <Icon name="alert" size={16} color="var(--warning, #F59E0B)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
                {t('products.barcodeDuplicate')}
              </p>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {dupProduct.name}
                {dupProduct.stock != null && ` · ${t('products.stockShort')}: ${dupProduct.stock}`}
              </p>
            </div>
          </div>
        )}
      </div>

      {isModuleOn(myShop, 'ownBarcodes') && (
        <div className="input-group">
          <label>{t('products.plu', 'Номер на весах (PLU)')}</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <input
              className="input"
              style={{ flex: 1, minWidth: 0 }}
              value={plu}
              onChange={(e) => setPlu(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
              placeholder={t('products.pluPlaceholder', 'напр. 18')}
              inputMode="numeric"
            />
            <button
              type="button"
              onClick={suggestFreePlu}
              className="btn btn-secondary"
              style={{ flexShrink: 0, width: 'auto', padding: '0 14px', whiteSpace: 'nowrap' }}
            >
              {t('products.pluSuggest', 'Свободный')}
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
            {t('products.pluHint', 'Весы отправляют этот номер и вес. Нужен весовым товарам (кг).')}
          </p>
        </div>
      )}

      {isModuleOn(myShop, 'marking') && (
        <div className="input-group">
          <label>{t('products.markingCode', 'Код маркировки')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              value={markingCode}
              onChange={(e) => setMarkingCode(e.target.value)}
              placeholder={t('products.markingPlaceholder', 'Сканируйте Честный знак')}
              inputMode="numeric"
            />
            <ScanButton
              raw
              onScan={(code) => setMarkingCode(extractGtin(code) || code)}
              size="lg"
            />
          </div>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
            {t('products.markingHint', 'Из Честного знака сохраняется GTIN — товар будет находиться и по нему')}
          </p>
        </div>
      )}
      </>)}

      {isModuleOn(myShop, 'categories') && (
        <div className="input-group">
          <label>{t('products.category')}</label>
          <button
            type="button"
            onClick={() => setCatSheetOpen(true)}
            className="input"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              textAlign: 'left', cursor: 'pointer',
            }}
          >
            <span style={{
              flex: 1,
              color: categoryId ? 'var(--fg)' : 'var(--fg-muted)',
            }}>
              {(() => {
                if (!categoryId) return t('products.categoryNone');
                const c = cats.find(x => x.id === categoryId);
                if (!c) return t('products.categoryNone');
                const parent = c.parentId ? cats.find(x => x.id === c.parentId) : null;
                return parent ? `${parent.name} · ${c.name}` : c.name;
              })()}
            </span>
            <Icon name="chevronDown" size={18} color="var(--fg-muted)" />
          </button>
        </div>
      )}

      {catSheetOpen && (
        <CategoryPickerSheet
          categories={cats}
          value={categoryId}
          onPick={setCategoryId}
          onClose={() => setCatSheetOpen(false)}
        />
      )}

      {/* СОСТАВ — для составных товаров (букеты, торты, наборы) */}
      {!isService && isModuleOn(myShop, 'composition') && (
      <div className="input-group">
        <label>
          {t('products.composition')}
          {components.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--fg-faint)', marginLeft: 6, fontWeight: 400 }}>
              · {components.length}
            </span>
          )}
        </label>

        {components.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginBottom: 8, lineHeight: 1.5 }}>
            {t('products.compositionHint')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            {components.map((c, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', borderRadius: 'var(--r-sm)',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              }}>
                {c._component?.photo ? (
                  <img src={c._component.photo} alt="" style={{ width: 32, height: 32, borderRadius: 'var(--r-xs)', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--r-xs)', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="package" size={14} color="var(--fg-faint)" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c._component?.name || c.componentId}
                  </p>
                  <p style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                    {unitLabel(c._component?.unit)}
                  </p>
                </div>
                <input
                  type="number"
                  inputMode="decimal"
                  className="input"
                  value={c.qty}
                  onChange={e => updateComponentQty(idx, e.target.value)}
                  style={{ width: 70, padding: '6px 8px', textAlign: 'right' }}
                />
                <button
                  onClick={() => removeComponent(idx)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--danger)', padding: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label={t('common.delete')}
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="btn btn-secondary btn-sm"
          style={{ width: '100%' }}
        >
          <Icon name="plus" size={14} stroke={2.5} />
          {t('products.addComponent')}
        </button>
      </div>
      )}

      {/* Единица измерения */}
      {!isService && (
      <div className="input-group">
        <label>{t('products.unit')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { code: 'шт', label: t('common.pieces') },
            { code: 'кг', label: t('common.kg') },
            { code: 'л',  label: t('common.liter') },
          ].map(u => (
            <button key={u.code} onClick={() => setUnit(u.code)} style={{
              flex: 1, padding: '10px', borderRadius: 'var(--r-sm)',
              border: '1px solid', borderColor: unit === u.code ? 'var(--brand-primary)' : 'var(--border)',
              background: unit === u.code ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
              color: unit === u.code ? 'var(--brand-primary)' : 'var(--fg-muted)',
              fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)',
            }}>{u.label}</button>
          ))}
        </div>
      </div>
      )}

      {/* Цены и остаток */}
      <div className="grid-2">
        <div className="input-group">
          <label>{t('products.costPrice')}</label>
          {/* Если есть компоненты и НЕТ ручного переопределения — показываем расчётное значение readonly */}
          {components.length > 0 && !costOverride ? (
            <>
              <input
                ref={costRef}
                className="input"
                type="number"
                value={calculatedCost.toFixed(2)}
                readOnly
                style={{ background: 'var(--bg-subtle)', color: 'var(--fg-muted)' }}
              />
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
                {t('products.calculatedCost')}: {formatMoney(calculatedCost, currency)}
              </p>
            </>
          ) : (
            <input
              ref={costRef}
              className="input"
              type="number"
              inputMode="decimal"
              defaultValue={product?.costPriceOverride ?? product?.costPrice ?? ''}
              placeholder="0"
            />
          )}
          {components.length > 0 && (
            <label style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={costOverride}
                onChange={e => setCostOverride(e.target.checked)}
                style={{ margin: 0 }}
              />
              {t('products.overrideCost')}
            </label>
          )}
        </div>
        <div className="input-group">
          <label>{t('products.salePrice')} *</label>
          <input ref={priceRef} className="input" type="number" inputMode="decimal" defaultValue={product?.salePrice || ''} placeholder="0" />
        </div>
      </div>
      {/* Остаток — только для НЕсоставных и НЕвариантных товаров */}
      {!isService && (components.length === 0 && !hasVariants ? (
        <div className="input-group">
          <label>{t('products.stock')}</label>
          <input ref={stockRef} className="input" type="number" inputMode="decimal" defaultValue={product?.stock || ''} placeholder="0" />
        </div>
      ) : components.length > 0 ? (
        <div className="input-group">
          <label>{t('products.available')}</label>
          <div style={{
            padding: '12px 14px', borderRadius: 'var(--r-sm)',
            background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
            fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon name="package" size={16} />
            <span>{t('products.canAssemble', { count: (() => {
              let min = Infinity;
              for (const c of components) {
                const need = Number(c.qty) || 0;
                const stock = Number(c._component?.stock) || 0;
                if (need <= 0) continue;
                const can = Math.floor(stock / need);
                if (can < min) min = can;
              }
              return isFinite(min) ? Math.max(0, min) : 0;
            })() })}</span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 6 }}>
            {t('products.compositionHint')}
          </p>
        </div>
      ) : null)}

      {/* Блок Варианты — только для НЕсоставных товаров */}
      {!isService && components.length === 0 && isModuleOn(myShop, 'variants') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hasVariants}
              onChange={e => {
                const next = e.target.checked;
                setHasVariants(next);
                if (next && attrs.length === 0) {
                  setAttrs([{ name: '', values: [] }]);
                }
              }}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                {t('variants.hasVariants')}
              </p>
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.5 }}>
                {t('variants.hasVariantsHint')}
              </p>
            </div>
          </label>

          {hasVariants && (
            <div style={{ marginTop: 14 }}>
              {/* Атрибуты */}
              <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                {t('variants.attributes')}
              </p>
              <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 10 }}>
                {t('variants.attributesHint')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {attrs.map((attr, idx) => (
                  <AttributeRow
                    key={`attr-${idx}`}
                    attr={attr}
                    onNameChange={(name) => updateAttrName(idx, name)}
                    onValuesChange={(raw) => updateAttrValuesRaw(idx, raw)}
                    onValuesBlur={(raw) => updateAttrValues(idx, raw)}
                    onRemove={() => removeAttribute(idx)}
                    t={t}
                  />
                ))}
              </div>

              {attrs.length < 3 && (
                <button
                  onClick={addAttribute}
                  className="btn btn-ghost"
                  style={{ width: '100%', marginTop: 10, fontSize: 13 }}
                >
                  <Icon name="plus" size={14} />
                  <span style={{ marginLeft: 6 }}>{t('variants.addAttribute')}</span>
                </button>
              )}

              <button
                onClick={generateMatrix}
                className="btn btn-secondary"
                style={{ width: '100%', marginTop: 10 }}
              >
                {variants.length > 0 ? t('variants.regenerate') : t('variants.generate')}
              </button>

              {/* Список вариантов */}
              {variants.length > 0 && (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
                    {t('variants.variantsList')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {variants.map((v, idx) => (
                      <VariantRow
                        key={v.id || `new-${idx}`}
                        variant={v}
                        currency={currency}
                        onEdit={() => setEditingVariantIdx(idx)}
                        onRemove={() => removeVariant(idx)}
                        t={t}
                      />
                    ))}
                  </div>

                  <button
                    onClick={addEmptyVariant}
                    className="btn btn-ghost"
                    style={{ width: '100%', marginTop: 8, fontSize: 13 }}
                  >
                    <Icon name="plus" size={14} />
                    <span style={{ marginLeft: 6 }}>{t('variants.addVariant')}</span>
                  </button>
                </>
              )}

              {variants.length === 0 && attrs.some(a => a.name && a.values.length > 0) && (
                <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 10, textAlign: 'center' }}>
                  {t('variants.noVariantsHint')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Показывать на витрине */}
      <div className="card" style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showInCatalog}
            onChange={e => setShowInCatalog(e.target.checked)}
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
              {t('products.showInCatalog')}
            </p>
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.5 }}>
              {t('products.showInCatalogHint')}
            </p>
          </div>
        </label>
      </div>

      {/* Основной поставщик (опционально) */}
      {!isService && isModuleOn(myShop, 'counterparties') && suppliers.length > 0 && (
        <div className="input-group">
          <label>{t('products.defaultSupplier')}</label>
          <select
            className="select"
            value={defaultSupplierId}
            onChange={e => setDefaultSupplierId(e.target.value)}
          >
            <option value="">— {t('common.notSet')} —</option>
            {suppliers.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
            {t('products.defaultSupplierHint')}
          </p>
        </div>
      )}

      <button className="btn btn-primary btn-lg" onClick={save} disabled={saving}>
        {saving ? t('products.saving') : t('products.save')}
      </button>

      {pickerOpen && (
        <ComponentPickerSheet
          allProducts={allProducts}
          excludeIds={[
            ...(product ? [product.id] : []),
            ...components.map(c => c.componentId),
          ]}
          onPick={addComponent}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {editingVariantIdx !== null && variants[editingVariantIdx] && (
        <VariantEditSheet
          variant={variants[editingVariantIdx]}
          attrs={attrs}
          currency={currency}
          productPhotos={photos}
          uploadFn={uploadApi.photos}
          onError={onError}
          onSave={(patch) => {
            updateVariant(editingVariantIdx, patch);
            setEditingVariantIdx(null);
          }}
          onClose={() => setEditingVariantIdx(null)}
          t={t}
          markingOn={isModuleOn(myShop, 'marking')}
        />
      )}
      {VariantPinModal}
    </div>
  );
}

// ─── AttributeRow — атрибут с UNCONTROLLED inputs ──────────
// Самый надёжный вариант для борьбы с Android IME / кириллицей:
// inputs ВООБЩЕ не управляются React state — никаких onChange.
// React больше не вмешивается в работу IME клавиатуры.
// Значения читаются через ref и отправляются в родителя только в onBlur.
function AttributeRow({ attr, onNameChange, onValuesChange, onValuesBlur, onRemove, t }) {
  const nameRef = useRef(null);
  const valuesRef = useRef(null);

  // Синхронизация defaultValue с пропсами при первом монтировании.
  // Дальше React НЕ трогает input — он живёт сам по себе.

  const handleNameBlur = () => {
    const v = nameRef.current?.value || '';
    onNameChange(v);
  };

  const handleValuesChange = () => {
    const v = valuesRef.current?.value || '';
    onValuesChange(v);  // мгновенное обновление для генератора
  };

  const handleValuesBlur = () => {
    const v = valuesRef.current?.value || '';
    onValuesBlur(v);
  };

  // Начальные значения
  const initialName = attr.name || '';
  const initialValues = attr._raw !== undefined ? attr._raw : (attr.values || []).join(', ');

  return (
    <div style={{
      padding: 10,
      background: 'var(--bg-subtle)',
      borderRadius: 'var(--r-xs)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input
          ref={nameRef}
          className="input"
          placeholder={t('variants.attributeNamePlaceholder')}
          defaultValue={initialName}
          onBlur={handleNameBlur}
          style={{ flex: 1 }}
        />
        <button
          onClick={onRemove}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--fg-muted)', padding: 6,
          }}
          aria-label={t('variants.removeAttribute')}
        >
          <Icon name="x" size={14} />
        </button>
      </div>
      <label style={{ fontSize: 11, color: 'var(--fg-muted)', display: 'block', marginBottom: 4 }}>
        {t('variants.values')}
      </label>
      <input
        ref={valuesRef}
        className="input"
        placeholder={t('variants.valuesPlaceholder')}
        defaultValue={initialValues}
        onChange={handleValuesChange}
        onBlur={handleValuesBlur}
      />
      <p style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}>
        {t('variants.valuesHint')}
      </p>
    </div>
  );
}

// ─── VariantRow — строка варианта в списке ──────────────────
function VariantRow({ variant, currency, onEdit, onRemove, t }) {
  const displayName = variant.name || Object.values(variant.attributeValues || {}).join(' / ') || t('variants.defaultVariant');
  return (
    <div
      onClick={onEdit}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
          {variant.isDefault && (
            <span style={{
              marginLeft: 6,
              fontSize: 9, fontWeight: 700,
              background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
              padding: '1px 5px', borderRadius: 4,
            }}>
              {t('variants.defaultVariant')}
            </span>
          )}
        </p>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(variant.salePrice || 0, currency)} · {t('products.stock')}: {variant.stock || 0}
          {variant.sku && <> · {variant.sku}</>}
        </p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)', padding: 6 }}
        aria-label={t('variants.removeVariant')}
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

// ─── VariantEditSheet — модалка редактирования варианта ────
function VariantEditSheet({ variant, attrs, currency, productPhotos = [], uploadFn, onError, onSave, onClose, t, markingOn = false }) {
  const [name, setName] = useState(variant.name || '');
  const [sku, setSku] = useState(variant.sku || '');
  const [barcode, setBarcode] = useState(variant.barcode || '');
  const [markingCode, setMarkingCode] = useState(variant.markingCode || '');
  const [salePrice, setSalePrice] = useState(variant.salePrice || '');
  const [costPrice, setCostPrice] = useState(variant.costPrice || '');
  const [stock, setStock] = useState(variant.stock || '');
  const [attributeValues, setAttributeValues] = useState(variant.attributeValues || {});
  const [photos, setPhotos] = useState(Array.isArray(variant.photos) ? variant.photos : []);

  const save = () => {
    onSave({
      name: name.trim(),
      sku: sku.trim() || null,
      barcode: barcode.trim() || null,
      markingCode: markingCode.trim() || null,
      salePrice: Number(salePrice) || 0,
      costPrice: Number(costPrice) || 0,
      stock: Number(stock) || 0,
      attributeValues,
      photos,
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'var(--bg)',
          borderRadius: '16px 16px 0 0',
          padding: 20,
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>{t('variants.editVariant')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Фото варианта (опционально — если пусто, используется фото товара) */}
        {uploadFn && (
          <div style={{ marginBottom: 16 }}>
            <PhotoUploader
              photos={photos}
              onChange={setPhotos}
              onError={onError}
              uploadFn={uploadFn}
            />
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
              {photos.length === 0 && productPhotos.length > 0
                ? t('variants.photosFallback')
                : t('variants.photosHint')}
            </p>
          </div>
        )}

        {/* Значения атрибутов */}
        {attrs.filter(a => a.name && String(a.name).trim()).map((attr) => {
          const attrName = String(attr.name).trim();
          return (
            <div key={attrName} className="input-group">
              <label>{attrName}</label>
              {attr.values.length > 0 ? (
                <select
                  className="input"
                  value={attributeValues[attrName] || ''}
                  onChange={e => setAttributeValues(prev => ({ ...prev, [attrName]: e.target.value }))}
                >
                  <option value="">—</option>
                  {attr.values.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  placeholder={t('variants.attributeValue')}
                  value={attributeValues[attrName] || ''}
                  onChange={e => setAttributeValues(prev => ({ ...prev, [attrName]: e.target.value }))}
                />
              )}
            </div>
          );
        })}

        <div className="input-group">
          <label>{t('variants.variantName')}</label>
          <input
            className="input"
            placeholder={Object.values(attributeValues).filter(Boolean).join(' / ')}
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label>{t('variants.variantSku')}</label>
          <input
            className="input"
            value={sku}
            onChange={e => setSku(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label>{t('products.barcode', 'Штрихкод')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder={t('products.barcodePlaceholder', 'EAN или GTIN, можно сканировать')}
              inputMode="numeric"
            />
            <ScanButton onScan={(code) => setBarcode(code)} size="lg" />
          </div>
        </div>

        {markingOn && (
          <div className="input-group">
            <label>{t('products.markingCode', 'Код маркировки')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                value={markingCode}
                onChange={(e) => setMarkingCode(e.target.value)}
                placeholder={t('products.markingPlaceholder', 'Сканируйте Честный знак')}
                inputMode="numeric"
              />
              <ScanButton raw onScan={(code) => setMarkingCode(extractGtin(code) || code)} size="lg" />
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="input-group">
            <label>{t('variants.variantPrice')}</label>
            <input
              className="input"
              type="number"
              inputMode="decimal"
              value={salePrice}
              onChange={e => setSalePrice(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>{t('variants.variantStock')}</label>
            <input
              className="input"
              type="number"
              inputMode="decimal"
              value={stock}
              onChange={e => setStock(e.target.value)}
            />
          </div>
        </div>

        <div className="input-group">
          <label>{t('variants.variantCostPrice')}</label>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            value={costPrice}
            onChange={e => setCostPrice(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={onClose} className="btn btn-ghost" style={{ flex: 1 }}>
            {t('common.cancel')}
          </button>
          <button onClick={save} className="btn btn-primary" style={{ flex: 1 }}>
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
